import { captureException } from '@sentry/react'
import { Form, Formik, FormikHelpers } from 'formik'
import { object, string } from 'yup'
import { Button } from '~/components/button'
import { TextInputField } from '~/components/text-input'
import { HeadingTW } from '~/components/typography/heading-tw'
import { useToasts } from '~/context/toasts'
import { useRecaptcha } from '~/hooks/use-recaptcha'
import { useSignInUser } from '~/hooks/use-sign-in-user'
import { useSelfServiceSignUpWithEmailMutation } from '~/pages/sign-up/graphql/self-service-sign-up-with-email'
import { apiError } from '~/utils/api-error'

import { LegalNotice } from './LegalNotice'
import { SelfServiceAuthLayout } from './SelfServiceAuthLayout'

type SelfServiceValues = {
  firstName: string
  lastName: string
  email: string
  password: string
}

const initialValues: SelfServiceValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
}

const schema = object<SelfServiceValues>({
  firstName: string().required('First name is required'),
  lastName: string().required('Last name is required'),
  email: string().email('Email is incomplete').required('Email is required'),
  password: string().required('Password is required'),
})

export const SelfServiceSignUp = () => {
  const [signUp] = useSelfServiceSignUpWithEmailMutation()
  const recaptcha = useRecaptcha()
  const signInUser = useSignInUser()
  const toasts = useToasts()

  const handleSubmit = async (
    values: SelfServiceValues,
    helpers: FormikHelpers<SelfServiceValues>
  ) => {
    try {
      const recaptchaResponse = await recaptcha.execute()
      const { data } = await signUp({
        variables: {
          input: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            recaptchaResponse,
          },
        },
      })

      if (!data) {
        throw new Error('Self-service sign-up returned no data')
      }

      await signInUser(data.selfServiceSignUpWithEmail.accessToken)
    } catch (error: any) {
      let handled = false

      apiError(error, {
        email_already_exists: () => {
          handled = true
          helpers.setFieldError('email', 'Email already exists')
        },
      })

      if (!handled) {
        captureException(error)
        toasts.error('Something went wrong. Please try again.')
      }

      recaptcha.reset()
    }
  }

  return (
    <SelfServiceAuthLayout>
      <HeadingTW className="mb-10 text-center text-4xl leading-[56px] tracking-[-1px]">
        Start with Kindest
      </HeadingTW>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={schema}
        validateOnBlur={false}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextInputField label="First Name" name="firstName" autoComplete="given-name" large />
              <TextInputField label="Last Name" name="lastName" autoComplete="family-name" large />
            </div>
            <TextInputField label="Email" name="email" autoComplete="email" marginTop={20} large />
            <TextInputField
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
              marginTop={20}
              marginBottom={32}
              large
            />
            <Button type="submit" loading={isSubmitting} size="large" width="100%">
              Continue
            </Button>
          </Form>
        )}
      </Formik>
      <LegalNotice />
    </SelfServiceAuthLayout>
  )
}
